import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IShellKeyCommand } from '@featit/shared';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { shell } from '@featit/shared/i18n/messages/en/shell';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT, type TFn } from '@/i18n/LocaleProvider';
import { useHistoryReach } from '@/desktop/desktopHistoryReach';
import { postShellMessage } from './shellBridge';
import { hasShellCapability, shellLeavesNavigationToWeb } from './shellDetection';
import { SHELL_KEY_COMMAND_EVENT } from './shellKeyCommandEvent';
import { goBackInHistory } from './backNavigation';

registerEnglishArea('shell', shell);

/** A command as the web lists it: `title` is its English, `i18n.title` the key the overlay reads. */
export interface IWebKeyCommand extends IShellKeyCommand {
  readonly i18n: { readonly title: MessageKey };
}

/**
 * The web's commands for the iPad's ⌘-hold shortcut overlay (F-0235).
 *
 * A key typed into the page already reaches the web's own shortcuts. What the page cannot do is be
 * listed in the system overlay: only a native `UIKeyCommand` is. So the web tells the shell which
 * commands to register, the shell registers them, and a chosen one comes back as `keyCommand { id }`.
 *
 * Only the app-wide commands are listed. A screen's own keys (`j`/`k`, `c`, `o`, `esc`) stay the
 * page's, because a native command would take the key away from the page everywhere.
 */
export const SHELL_WEB_KEY_COMMANDS: readonly IWebKeyCommand[] = [
  {
    id: 'palette',
    title: 'Search and commands',
    i18n: { title: 'shell.keyCommands.palette' },
    input: 'k',
    modifiers: ['command'],
  },
  {
    id: 'new-item',
    title: 'New work item',
    i18n: { title: 'shell.keyCommands.newItem' },
    input: 'n',
    modifiers: ['command'],
  },
  {
    id: 'search',
    title: 'Find work items',
    i18n: { title: 'shell.keyCommands.search' },
    input: 'f',
    modifiers: ['command'],
  },
];

/**
 * I-1304 (IL-17): Back and Forward, the keys every Apple browser gives them, listed only by a shell
 * that leaves the navigation to the web (the iPad app). Where the shell draws its own bar, its back
 * is the bar's, and the window's history is not the reader's way back.
 */
export const SHELL_WEB_HISTORY_KEY_COMMANDS: readonly IWebKeyCommand[] = [
  {
    id: 'back',
    title: 'Back',
    i18n: { title: 'shell.keyCommands.back' },
    input: '[',
    modifiers: ['command'],
  },
  {
    id: 'forward',
    title: 'Forward',
    i18n: { title: 'shell.keyCommands.forward' },
    input: ']',
    modifiers: ['command'],
  },
];

/** The commands as the shell receives them: each titled in the reader's language (I-2362). */
export function shellKeyCommandsIn(
  t: TFn,
  commands: readonly IWebKeyCommand[],
): IShellKeyCommand[] {
  return commands.map(({ id, i18n, input, modifiers }) => ({
    id,
    title: t(i18n.title),
    input,
    modifiers,
  }));
}

export type ShellWebKeyCommandIdType = 'palette' | 'new-item' | 'search';

/**
 * Lists the commands with the shell once, and runs a chosen one through `handlers`. Nothing happens
 * in a browser, or in an app build without the `keyCommands` capability.
 */
export function useShellKeyCommands(handlers: Record<ShellWebKeyCommandIdType, () => void>): void {
  const latest = useRef(handlers);
  latest.current = handlers;
  const navigate = useNavigate();
  const reach = useHistoryReach();
  const history = useRef({ navigate, reach });
  history.current = { navigate, reach };
  const t = useT();

  // Listed again in the reader's new language when it changes: the shell replaces its list (I-2362).
  useEffect(() => {
    if (!hasShellCapability('keyCommands')) return;
    postShellMessage({
      type: 'keyCommands',
      commands: shellKeyCommandsIn(
        t,
        shellLeavesNavigationToWeb()
          ? [...SHELL_WEB_KEY_COMMANDS, ...SHELL_WEB_HISTORY_KEY_COMMANDS]
          : SHELL_WEB_KEY_COMMANDS,
      ),
    });
  }, [t]);

  useEffect(() => {
    if (!hasShellCapability('keyCommands')) return undefined;
    const withHistory = shellLeavesNavigationToWeb();
    const run = (event: Event) => {
      const id = (event as CustomEvent<{ id: string }>).detail?.id;
      if (id === 'palette' || id === 'new-item' || id === 'search') latest.current[id]();
      if (!withHistory) return;
      // As the buttons do: nothing when there is nowhere to go, rather than leaving the app's page.
      if (id === 'back' && history.current.reach.canGoBack)
        goBackInHistory(history.current.navigate);
      if (id === 'forward' && history.current.reach.canGoForward) history.current.navigate(1);
    };
    window.addEventListener(SHELL_KEY_COMMAND_EVENT, run);
    return () => window.removeEventListener(SHELL_KEY_COMMAND_EVENT, run);
  }, []);
}
